import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '../lib/api'
import type { MatchResult } from '../types/api'
import Button from '../components/ui/Button'
import ScoreBadge from '../components/ui/ScoreBadge'

export default function MatchReportDetail() {
  const { jobId } = useParams<{ jobId: string }>()
  const navigate = useNavigate()
  const [matches, setMatches] = useState<MatchResult[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [jobTitle, setJobTitle] = useState<string>('')
  const [employer, setEmployer] = useState<string>('')
  const [createdAt, setCreatedAt] = useState<string>('')
  const [sendingQuestions, setSendingQuestions] = useState<string | null>(null)
  const [matchStatuses, setMatchStatuses] = useState<Record<string, boolean>>({})

  useEffect(() => {
    if (!jobId) {
      setError('No job ID provided')
      setLoading(false)
      return
    }

    // Load report data
    api.jobs.matches.getLatest(jobId)
      .then((res) => {
        setMatches(res.data.matches)
        setJobTitle(res.data.title)
        setEmployer(res.data.employer)
        setCreatedAt(res.data.created_at)
        setLoading(false)
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load report')
        setLoading(false)
      })
  }, [jobId])

  useEffect(() => {
    if (!matches) return
    Promise.all(
      matches.map(async (m) => {
        try {
          const res = await api.telegram.getStatus(m.id)
          return [m.id, res.data.connected] as [string, boolean]
        } catch {
          return [m.id, false] as [string, boolean]
        }
      })
    ).then((entries) => setMatchStatuses(Object.fromEntries(entries)))
  }, [matches])

  const handleSendQuestions = async (profileId: string) => {
    if (!jobId) return
    setSendingQuestions(profileId)
    try {
      await api.telegram.sendQuestions(jobId, profileId)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send questions')
    } finally {
      setSendingQuestions(null)
    }
  }

  if (loading) return <p className="text-text-muted text-sm">Loading report...</p>
  if (error) return <p className="text-red-400 text-sm">{error}</p>
  if (!matches) return <p className="text-text-muted text-sm">Not found.</p>

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/admin/match-reports')}
          className="text-xs text-text-muted hover:text-primary tracking-tactical"
        >
          ← MATCH REPORTS
        </button>
      </div>

      <div>
        <h1 className="text-2xl">MATCH REPORT</h1>
        {createdAt && <p className="text-xs text-text-muted mt-1">Generated {new Date(createdAt).toLocaleString()}</p>}
        {jobTitle && <p className="text-sm text-foreground mt-2">{jobTitle}</p>}
        {employer && <p className="text-sm text-text-muted">{employer}</p>}
      </div>

      <div className="border border-border-subtle p-6">
        {matches.length === 0 ? (
          <p className="text-text-muted text-sm">No candidates found.</p>
        ) : (
          <ul className="space-y-6">
            {matches.map((m, i) => (
              <li key={m.id} className="border-b border-border-subtle pb-6 last:border-0 last:pb-0">
                <div className="flex items-start gap-4">
                  <div className="text-3xl font-bold text-primary">#{i + 1}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-xl font-medium">{m.name}</h3>
                      <ScoreBadge score={m.score} />
                    </div>
                    <div className="mb-4">
                      <p className="text-xs text-text-muted tracking-tactical mb-2">WHY THEY'RE A GOOD FIT</p>
                      {m.reasons.length > 0 ? (
                        <ul className="space-y-1">
                          {m.reasons.map((r, ri) => (
                            <li key={ri} className="text-sm text-foreground">• {r}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-text-muted italic">No detailed reasons provided</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        onClick={() => navigate(`/admin/applicants/${m.id}`)}
                        className="text-xs"
                      >
                        VIEW PROFILE
                      </Button>
                      {matchStatuses[m.id] && (
                        <Button
                          variant="ghost"
                          onClick={() => handleSendQuestions(m.id)}
                          disabled={sendingQuestions === m.id}
                          className="text-xs"
                        >
                          {sendingQuestions === m.id ? 'SENDING...' : 'SEND QUESTIONS'}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
